import React from "react";
import { Target, Eye, MapPin, ShieldCheck, FileCheck2 } from "lucide-react";
import { Container, Reveal, SectionHeading, Overline } from "../components/common";
import { PageHero } from "../components/PageHero";
import { Marquee } from "../components/Marquee";
import { IndiaMap } from "../components/IndiaMap";
import { ICONS } from "../components/icons";
import { IMAGES, TRANSPARENCY, PROGRAMS } from "../lib/content";
import { fetchPrograms } from "../lib/api";
import { mapProgramRows } from "../lib/dynamicMappers";
import { useApiData } from "../hooks/useApiData";

const VM = [
  {
    icon: Eye,
    title: "Our Vision",
    text: "A society where every underprivileged child, girl child, single woman and elder lives with dignity, opportunity and hope — regardless of where they were born.",
  },
  {
    icon: Target,
    title: "Our Mission",
    text: "To heal hearts with helping hands through sustainable programs in education, healthcare, skill development and women empowerment, delivered directly at the grassroots across Telangana & Andhra Pradesh, with initiatives expanding throughout India.",
  },
];

const PillarsSection = () => {
  const programs = useApiData(fetchPrograms, PROGRAMS, mapProgramRows);

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="about-pillars">
      <Container>
        <SectionHeading
          overline="Our 6 Pillars"
          title="Six programs, one whole-community mission"
          subtitle="Every pillar tackles a different need — together they form a complete circle of care."
          center
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((p, i) => {
            const Icon = ICONS[p.icon];
            return (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-3xl bg-offwhite border border-slate-100 p-7 hover:shadow-card transition-all">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-teal-50 text-teal-700">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-navy">{p.name}</h3>
                  <div className="mt-1 font-mono text-xs uppercase tracking-wider text-coral">{p.tag}</div>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export default function About() {
  return (
    <>
      <PageHero
        overline="About Janma Foundation"
        title="A grassroots story that began in 2014"
        subtitle="Twelve years of working shoulder-to-shoulder with the communities we serve — building trust, one village at a time."
        image={IMAGES.womenStanding}
      />

      <section className="py-20 lg:py-28 bg-white" data-testid="about-story">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <Reveal className="lg:col-span-6 order-2 lg:order-1">
              <Overline>Our Story</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-navy leading-tight">
                Born from empathy, built on the ground
              </h2>
              <div className="mt-6 space-y-4 text-slate-600 text-lg leading-relaxed">
                <p>
                  Janma Foundation was established in 2014 with a simple conviction: real change
                  does not trickle down — it must be carried, by hand, into the villages and
                  homes where it is needed most.
                </p>
                <p>
                  What started with a handful of volunteers supporting a single government school
                  has grown into a movement spanning healthcare camps, skill centres, orphanage
                  support, and full-paid scholarships for girl children — rooted primarily across
                  Telangana and Andhra Pradesh, and now extending our helping hands throughout
                  India.
                </p>
                <p>
                  We remain fiercely grassroots — no middle agents, no distant offices. Just
                  people helping people, with complete transparency and accountability.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden aspect-[4/5] shadow-card">
                  <img src={IMAGES.children} alt="Children supported by Janma Foundation" className="h-full w-full object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl bg-coral text-white px-6 py-5 shadow-glow">
                  <div className="font-display text-3xl font-extrabold">12</div>
                  <div className="text-sm text-white/90">years of impact</div>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <Marquee dark />

      <section className="py-20 lg:py-28 bg-offwhite" data-testid="about-vision-mission">
        <Container>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {VM.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-3xl bg-white p-8 lg:p-10 shadow-soft border border-slate-100">
                  <span className="grid place-items-center h-14 w-14 rounded-2xl bg-teal-50 text-teal-700">
                    <v.icon className="h-7 w-7" />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-bold text-navy">{v.title}</h3>
                  <p className="mt-4 text-slate-600 text-lg leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <PillarsSection />

      <section className="py-20 lg:py-28 bg-white" data-testid="about-regions">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <Reveal className="lg:col-span-5">
              <Overline>Where We Work</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-navy leading-tight">
                Rooted in Telangana & Andhra Pradesh, growing across India
              </h2>
              <p className="mt-5 text-slate-600 text-lg leading-relaxed">
                Our teams operate deep in rural districts of Telangana & Andhra Pradesh — from
                government schools and health camps to skill centres and orphanages — while our
                network of partners and volunteers carries the same mission to communities across
                India.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-4">
                {[
                  { name: "Telangana", tag: "Active districts" },
                  { name: "Andhra Pradesh", tag: "Active districts" },
                  { name: "Pan-India", tag: "Growing reach" },
                ].map((r) => (
                  <div key={r.name} className="rounded-2xl bg-teal-50 p-5">
                    <MapPin className="h-6 w-6 text-teal-700" />
                    <div className="mt-3 font-display font-bold text-navy">{r.name}</div>
                    <div className="text-sm text-slate-500">{r.tag}</div>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="relative rounded-3xl bg-teal-900 p-8 sm:p-10 overflow-hidden" data-testid="regions-map">
                <div className="absolute inset-0 grain opacity-[0.08]" />
                <span className="relative inline-flex rounded-full bg-white/10 glass border border-white/20 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-teal-100">
                  All-India footprint
                </span>
                <div className="relative mt-4">
                  <IndiaMap />
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <section className="py-20 lg:py-28 bg-teal-900 relative overflow-hidden" data-testid="about-transparency">
        <div className="absolute inset-0 grain opacity-[0.07]" />
        <Container className="relative">
          <SectionHeading
            overline="Transparency & Legal"
            title="Fully compliant. Completely accountable."
            subtitle="Registered and certified so your contribution is safe, tax-exempt and traceable."
            light
            center
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TRANSPARENCY.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.06}>
                <div className="flex items-start gap-4 rounded-2xl bg-white/5 border border-white/10 p-6">
                  <FileCheck2 className="h-6 w-6 text-coral shrink-0" />
                  <div>
                    <div className="font-mono text-xs uppercase tracking-wider text-teal-200">{t.label}</div>
                    <div className="mt-1 font-display font-bold text-white text-lg">{t.value}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-8 flex items-center justify-center gap-3 text-teal-100 text-sm">
              <ShieldCheck className="h-5 w-5 text-coral" />
              Donations eligible for 80G tax exemption · Bank details shared on request
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
